import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Trash2, RotateCcw, Search, AlertTriangle } from 'lucide-react';

export const TrashTab = ({ isItalian }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const fetchTrash = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getDeletedUsers({ search: search || undefined, limit: 200 });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Errore caricamento cestino'); }
    setLoading(false);
  };

  useEffect(() => { fetchTrash(); }, []);

  const handleRestore = async (userId, name) => {
    try {
      await adminApi.restoreUser(userId);
      toast.success(isItalian ? `${name} ripristinato` : `${name} restored`);
      fetchTrash();
    } catch { toast.error('Errore ripristino'); }
  };

  const handleBulkRestore = async () => {
    try {
      await adminApi.bulkRestoreUsers([...selected]);
      toast.success(isItalian ? `${selected.size} utenti ripristinati` : `${selected.size} users restored`);
      setSelected(new Set());
      fetchTrash();
    } catch { toast.error('Errore ripristino'); }
  };

  const handlePermanentDelete = async (userId, name) => {
    if (!window.confirm(isItalian ? `Eliminare DEFINITIVAMENTE ${name}? Questa azione non può essere annullata.` : `Permanently delete ${name}? This cannot be undone.`)) return;
    try {
      await adminApi.permanentDeleteUser(userId);
      toast.success(isItalian ? `${name} eliminato definitivamente` : `${name} permanently deleted`);
      fetchTrash();
    } catch { toast.error('Errore eliminazione'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map(u => u.user_id)));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trash2 size={18} className="text-[#FF1744]" />
          <h3 className="text-lg font-bold text-white">
            {isItalian ? 'Cestino' : 'Trash'} ({total})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder={isItalian ? 'Cerca...' : 'Search...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTrash()}
              className="pl-8 bg-[#050508] border-white/10 text-white w-48"
              data-testid="trash-search"
            />
          </div>
          {selected.size > 0 && (
            <Button size="sm" onClick={handleBulkRestore} className="bg-[#00E676] hover:bg-[#00E676]/80 text-black" data-testid="bulk-restore-btn">
              <RotateCcw size={14} className="mr-1" />
              {isItalian ? `Ripristina (${selected.size})` : `Restore (${selected.size})`}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500">{isItalian ? 'Caricamento...' : 'Loading...'}</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Trash2 size={32} className="mx-auto mb-3 opacity-30" />
          <p>{isItalian ? 'Il cestino è vuoto' : 'Trash is empty'}</p>
        </div>
      ) : (
        <div className="border border-white/10 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#050508] text-slate-400 text-xs uppercase tracking-wider">
                <th className="p-3 text-left w-8">
                  <input type="checkbox" checked={selected.size === users.length && users.length > 0} onChange={toggleAll} className="accent-[#00F0FF]" />
                </th>
                <th className="p-3 text-left">{isItalian ? 'Nome' : 'Name'}</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">{isItalian ? 'Ruolo' : 'Role'}</th>
                <th className="p-3 text-left">{isItalian ? 'Eliminato il' : 'Deleted at'}</th>
                <th className="p-3 text-right">{isItalian ? 'Azioni' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleSelect(u.user_id)} className="accent-[#00F0FF]" />
                  </td>
                  <td className="p-3 text-white font-medium">{u.gamertag || u.name || '-'}</td>
                  <td className="p-3 text-slate-400">{u.email}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${u.role === 'captain' ? 'bg-[#FFEA00]/20 text-[#FFEA00]' : 'bg-white/10 text-slate-300'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="p-3 text-slate-500 text-xs">{u.deleted_at ? new Date(u.deleted_at).toLocaleDateString('it-IT') : '-'}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleRestore(u.user_id, u.gamertag || u.name)} className="text-[#00E676] hover:bg-[#00E676]/10" data-testid={`restore-${u.user_id}`}>
                        <RotateCcw size={14} className="mr-1" />
                        {isItalian ? 'Ripristina' : 'Restore'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePermanentDelete(u.user_id, u.gamertag || u.name)} className="text-[#FF1744] hover:bg-[#FF1744]/10" data-testid={`perm-delete-${u.user_id}`}>
                        <AlertTriangle size={14} className="mr-1" />
                        {isItalian ? 'Elimina per sempre' : 'Delete forever'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
